<template>
  <v-app :class="pageClass">
    <transition name="slide-up">
      <div v-if="isLoading" class="loading-overlay">
        <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="80" r="10" fill="#e20034">
            <animate attributeName="cy" values="80; 20; 80" dur="1s" repeatCount="indefinite" keyTimes="0; 0.3; 1"
              keySplines=".5 0 .5 1; .5 0 .5 1" calcMode="spline" />
          </circle>
        </svg>
      </div>
    </transition>

    <div v-if="isMobile && isLandscape" class="landscape-warning">
      <p>Please rotate your device to portrait mode for the best experience.</p>
    </div>
    <div v-else>
      <AppMenu />
      <v-main>
        <router-view :class="{ withMenu: menuOpen }" />
      </v-main>
    </div>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useGlobalStore } from '@/stores/global';
import AppMenu from '@/components/AppMenu';
import { useStructuredData } from '@/composables/useStructuredData';

const { addStructuredData } = useStructuredData();

const menuOpen = ref(false);
const route = useRoute();
const globalStore = useGlobalStore();
const isLoading = ref(true);
const isMobile = ref(/Mobi|Android/i.test(navigator.userAgent));
const isLandscape = ref(window.matchMedia("(orientation: landscape)").matches);



const pageClass = computed(() => route.meta.pageClass || '');

const checkOrientation = () => {
  isLandscape.value = window.matchMedia("(orientation: landscape)").matches;
};

// Dismiss loading overlay after initial page render
const dismissLoading = async () => {
  globalStore.fetchData();
  await nextTick();
  isLoading.value = false;
};

onMounted(async () => {
  // Add global structured data for GEO (Generative Engine Optimization)
  const globalOrganizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.ignis.co.uk/#organization',
    name: 'Ignis Ltd',
    legalName: 'Ignis Limited',
    alternateName: ['Ignis Agency', 'Ignis Marketing'],
    url: 'https://www.ignis.co.uk',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.ignis.co.uk/favicon.ico',
      width: 512,
      height: 512
    },
    description: 'Award-winning experiential marketing agency specializing in brand experiences, digital campaigns, and consumer engagement. We\'ve been a \'start-up\' since 1988, constantly challenging and punching for some of the world\'s most exciting brands.',
    slogan: 'The home of upstart thinking',
    foundingDate: '1988',
    founders: [
      {
        '@type': 'Person',
        name: 'Ignis Founders'
      }
    ],
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: '50-100'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'St. John\'s Hall, 374 North End Road, Fulham',
      addressLocality: 'London',
      addressRegion: 'Greater London',
      postalCode: 'SW6 1LY',
      addressCountry: {
        '@type': 'Country',
        name: 'United Kingdom'
      }
    },
    location: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'St. John\'s Hall, 374 North End Road, Fulham',
        addressLocality: 'London',
        addressRegion: 'Greater London',
        postalCode: 'SW6 1LY',
        addressCountry: {
          '@type': 'Country',
          name: 'United Kingdom'
        }
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 51.4677,
        longitude: -0.2019
      }
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+44-207-385-6677',
        contactType: 'Customer Service',
        email: 'jackie@ignis.co.uk',
        availableLanguage: ['English'],
        areaServed: ['GB', 'EU', 'US']
      },
      {
        '@type': 'ContactPoint',
        contactType: 'Employment',
        email: 'jackie@ignis.co.uk',
        description: 'Career opportunities and job applications'
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/ignis-ltd/',
      'https://www.instagram.com/ignisagency',
      'https://www.tiktok.com/@ignisagency'
    ],
    serviceArea: {
      '@type': 'Country',
      name: 'United Kingdom'
    },
    areaServed: ['United Kingdom', 'Europe', 'United States'],
    knowsAbout: [
      'Experiential Marketing',
      'Brand Experience',
      'Digital Marketing',
      'Event Marketing',
      'Consumer Engagement',
      'Brand Strategy',
      'Creative Services',
      'Sampling Campaigns',
      'Festival Marketing',
      'Trade Marketing',
      'Content Marketing',
      'Social Media Marketing',
      'Influencer Marketing',
      'Brand Activation',
      'Corporate Training',
      'E-Learning Development'
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Ignis Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Experiential Marketing',
            description: 'Immersive brand experiences that create lasting connections with consumers'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Digital Campaigns',
            description: 'Comprehensive digital marketing campaigns across all channels'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Brand Strategy',
            description: 'Strategic brand positioning and messaging development'
          }
        }
      ]
    },
    award: [
      'Digital Impact Award',
      'RAR Award',
      'Industry Recognition Awards',
      'Human Rights Excellence Award',
      'Corporate Responsibility Award'
    ],
    employee: [
      {
        '@type': 'Person',
        name: 'Jackie',
        jobTitle: 'Business Development',
        email: 'jackie@ignis.co.uk'
      }
    ],
    memberOf: {
      '@type': 'Organization',
      name: 'Employee Owned Association',
      description: 'Proudly employee owned company'
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Business Model',
        value: 'Employee Owned'
      },
      {
        '@type': 'PropertyValue',
        name: 'Agency Type',
        value: 'Experiential Marketing Agency'
      },
      {
        '@type': 'PropertyValue',
        name: 'Established',
        value: '1988'
      },
      {
        '@type': 'PropertyValue',
        name: 'Location',
        value: 'London, UK'
      }
    ]
  };

  // Add Website structured data for GEO
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://www.ignis.co.uk/#website',
    url: 'https://www.ignis.co.uk',
    name: 'Ignis Ltd - The home of upstart thinking',
    alternateName: 'Ignis Agency',
    description: 'Award-winning experiential marketing agency delivering impactful brand experiences since 1988',
    publisher: {
      '@id': 'https://www.ignis.co.uk/#organization'
    },
    inLanguage: 'en-GB',
    copyrightYear: 2025,
    copyrightHolder: {
      '@id': 'https://www.ignis.co.uk/#organization'
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.ignis.co.uk/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  // Add Professional Service structured data for GEO
  const professionalServiceData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': 'https://www.ignis.co.uk/#service',
    name: 'Ignis Ltd - Experiential Marketing Agency',
    image: 'https://ignis-vids.s3.eu-west-1.amazonaws.com/meta_poster.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'St. John\'s Hall, 374 North End Road, Fulham',
      addressLocality: 'London',
      addressRegion: 'Greater London',
      postalCode: 'SW6 1LY',
      addressCountry: {
        '@type': 'Country',
        name: 'United Kingdom'
      }
    },
    telephone: '+44-207-385-6677',
    url: 'https://www.ignis.co.uk',
    priceRange: '£££',
    additionalType: 'https://schema.org/MarketingAgency',
    areaServed: 'United Kingdom',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Marketing Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Experiential Marketing',
            category: 'Marketing',
            provider: {
              '@id': 'https://www.ignis.co.uk/#organization'
            }
          }
        }
      ]
    }
  };

  // Add all structured data for better GEO
  addStructuredData(globalOrganizationData);
  addStructuredData(websiteData);
  addStructuredData(professionalServiceData);

  window.addEventListener("resize", checkOrientation);
  await dismissLoading();
});

onUnmounted(() => {
  window.removeEventListener("resize", checkOrientation);
});

</script>


<style lang="scss">
@import '@/assets/scss/style';

$panel-brands-bg-color: #E20034;
$text-light: #ffffff;
$text-dark: #000000;

@function best-text-color($bg-color, $light-color, $dark-color) {
  $lightness: lightness($bg-color);
  @return if($lightness > 50%, $dark-color, $light-color);
}

.header {
  height: 58px;
}

.page-case-study .footer__branding-container {
  height: unset;
}

.landscape-warning {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-family: "Bison" sans-serif;
  z-index: 9999;
  text-transform: uppercase;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.6s ease-in-out;
  z-index: 19999;

  h2 {
    color: #555555;
  }
}

.loader {
  font-size: 1.5rem;
  font-weight: bold;
  color: #555555;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.6s ease-in-out, opacity 0.6s ease-in-out;
}

.slide-up-leave-active {
  pointer-events: none;
}

.slide-up-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
